import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShoppingCart, Search, Plus, Facebook, Instagram, Youtube } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import CartSidebar from '@/components/CartSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MenuPage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { addToCart, getCartCount, isCartOpen, setIsCartOpen } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMenuItems();
  }, []);

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu-items`);
      if (response.data.length === 0) {
        await axios.post(`${API}/init-menu`);
        const newResponse = await axios.get(`${API}/menu-items`);
        setMenuItems(newResponse.data);
      } else {
        setMenuItems(response.data);
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to load menu items');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (item) => {
    addToCart(item);
    toast.success(`${item.name} added to cart!`);
  };

  const filteredItems = menuItems.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.ingredients.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f9f7ed' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto" style={{ borderColor: '#a80707' }}></div>
          <p className="mt-4 text-lg" style={{ color: '#a80707' }}>Loading menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f7ed' }}>
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="w-24"></div>
            <div className="flex items-center justify-center flex-1">
              <a href="https://foufoubros.com">
                <img 
                  src="https://customer-assets.emergentagent.com/job_easyfeast-2/artifacts/i686abyr_logo%20Fufos%20bro%20.webp" 
                  alt="Foufou Bros Logo" 
                  className="h-16 w-auto cursor-pointer hover:opacity-90 transition-opacity"
                />
              </a>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => setIsCartOpen(!isCartOpen)}
                className="relative text-white font-semibold"
                style={{ backgroundColor: '#a80707' }}
                data-testid="cart-toggle-btn"
              >
                <ShoppingCart className="h-5 w-5" />
                {getCartCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center" style={{ color: '#a80707' }} data-testid="cart-count">
                    {getCartCount()}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16" style={{ backgroundColor: '#f9f7ed' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ color: '#a80707' }}>
            You're in! We deliver straight to your door
          </h2>
          <p className="text-lg sm:text-xl mb-8" style={{ color: '#a80707', opacity: 0.8 }}>
            Time to eat clean and feel unstoppable.
          </p>
          <div className="max-w-md mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 py-6 text-base border-2"
                style={{ borderColor: '#a80707' }}
                data-testid="menu-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Menu Items Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h3 className="text-3xl font-bold mb-8" style={{ color: '#a80707' }}>Our Best Sellers</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="menu-item-card bg-white rounded-2xl overflow-hidden shadow-lg"
              data-testid={`menu-item-${item.id}`}
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c';
                  }}
                />
                <div className="absolute top-3 right-3 bg-white px-3 py-1 rounded-full shadow-md">
                  <span className="font-bold" style={{ color: '#a80707' }}>CA${item.price.toFixed(2)}</span>
                </div>
              </div>
              <div className="p-6">
                <h4 className="text-xl font-bold mb-1" style={{ color: '#a80707' }} data-testid={`item-name-${item.id}`}>{item.name}</h4>
                <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                <div className="rounded-lg p-3 mb-4" style={{ backgroundColor: '#f9f7ed' }}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-gray-700">{item.calories}</span>
                    <span className="text-gray-600">{item.protein} / {item.carbs} / {item.fat}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-4 line-clamp-2">{item.ingredients}</p>
                <Button
                  onClick={() => handleAddToCart(item)}
                  className="w-full add-to-cart-btn text-white font-semibold py-6"
                  style={{ backgroundColor: '#a80707' }}
                  data-testid={`add-to-cart-btn-${item.id}`}
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add to Cart
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-12 mt-16" style={{ backgroundColor: '#a80707' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Hours */}
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold mb-4 flex items-center justify-center md:justify-start gap-2">
                <span className="text-2xl">⟡</span> HOURS <span className="text-2xl">⟡</span>
              </h3>
              <p className="text-sm opacity-90">- Delivery on Sundays</p>
              <p className="text-sm opacity-90">- Delivery on Wednesdays (for our premium pack customers)</p>
            </div>

            {/* Location */}
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold mb-4 flex items-center justify-center md:justify-start gap-2">
                <span className="text-2xl">⟡</span> LOCATION <span className="text-2xl">⟡</span>
              </h3>
              <p className="text-sm opacity-90">- Montreal</p>
              <p className="text-sm opacity-90">- North Shore of Montreal</p>
              <p className="text-sm opacity-90">- South Shore of Montreal</p>
            </div>

            {/* Follow */}
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold mb-4 flex items-center justify-center md:justify-start gap-2">
                <span className="text-2xl">⟡</span> FOLLOW <span className="text-2xl">⟡</span>
              </h3>
              <div className="flex gap-4 justify-center md:justify-start">
                <a 
                  href="https://www.facebook.com/share/16NQWXeF3c/?mibextid=wwXIfr" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  aria-label="Visit our Facebook page"
                >
                  <Facebook className="h-6 w-6" />
                </a>
                <a 
                  href="https://www.instagram.com/les_foufou_bros?igsh=OTF1MXNpeXV4a3Br&utm_source=qr" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  aria-label="Visit our Instagram page"
                >
                  <Instagram className="h-6 w-6" />
                </a>
                <a 
                  href="https://youtube.com/@foufoubros?si=Mdom_LJR6SpdNWyO" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  aria-label="Visit our YouTube channel"
                >
                  <Youtube className="h-6 w-6" />
                </a>
                <a 
                  href="https://www.tiktok.com/@foufoubros?_t=ZS-90KYAhQXQOW&_r=1" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  aria-label="Visit our TikTok page"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="text-center pt-8 border-t border-white border-opacity-20">
            <button className="border-2 border-white px-8 py-3 text-lg font-semibold hover:bg-white transition-colors" style={{ color: 'white' }} onMouseEnter={(e) => e.target.style.color = '#a80707'} onMouseLeave={(e) => e.target.style.color = 'white'}>
              CONTACT US NOW
            </button>
          </div>

          <div className="text-center mt-8">
            <p className="text-sm opacity-75">PRIVATE PREP BY</p>
            <a href="https://foufoubros.com" className="inline-block">
              <img 
                src="https://customer-assets.emergentagent.com/job_easyfeast-2/artifacts/i686abyr_logo%20Fufos%20bro%20.webp" 
                alt="Foufou Bros" 
                className="h-12 mx-auto mt-2 brightness-0 invert cursor-pointer hover:opacity-80 transition-opacity"
              />
            </a>
          </div>

          {/* Admin Link */}
          <div className="text-center mt-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-white hover:text-white hover:bg-white hover:bg-opacity-20 text-sm"
              data-testid="admin-link"
            >
              Admin Dashboard
            </Button>
          </div>
        </div>
      </footer>

      {/* Cart Sidebar */}
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default MenuPage;